import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useRef, useState, useEffect, useLayoutEffect, } from "react";
import { createPortal } from "react-dom";
import { Box, Flex, CloseButton } from "@chakra-ui/react";
// Custom bottom-sheet drawer. Built without Chakra's Drawer / framer-motion to
// avoid fighting framer-motion's hardcoded enter/exit variants — those ignore
// any inline transform set during drag, which made the drawer visibly snap
// back to translateY(0) on close. Owning the DOM and animations directly
// gives us native-feeling 1:1 finger tracking and a smooth velocity-aware
// close.
const OPEN_TRANSITION = "transform 0.32s cubic-bezier(0.32, 0.72, 0, 1), height 0.25s ease-out";
const FADE_TRANSITION = "opacity 0.24s ease-out";
const SNAP_BACK_TRANSITION = "transform 0.28s cubic-bezier(0.32, 0.72, 0, 1), height 0.25s ease-out";
const SwipeDownDrawer = ({ isOpen, onClose, content, header, footer, showCloseButton = false, defaultHeight, bg = "#1E1E1E", color = "white", mode, topText, }) => {
    const sheetRef = useRef(null);
    const backdropRef = useRef(null);
    const bodyRef = useRef(null);
    const touchStartYRef = useRef(0);
    const touchStartTimeRef = useRef(0);
    const translateYRef = useRef(0);
    const isDraggingRef = useRef(false);
    const rafIdRef = useRef(null);
    const closeTimeoutRef = useRef(null);
    const openDelayTimeoutRef = useRef(null);
    // `mounted` controls portal mount; `open` controls visual open state.
    // Two-step pattern: mount → next frame → set open=true → CSS transition.
    // On close: open=false → CSS transition → unmount after duration.
    const [mounted, setMounted] = useState(isOpen);
    const [open, setOpen] = useState(false);
    const isPaxMode = mode === "pax";
    const checkIsAtTop = useCallback(() => {
        if (bodyRef.current) {
            return bodyRef.current.scrollTop === 0;
        }
        return true;
    }, []);
    // Sync external isOpen → mounted/open with proper ordering.
    useEffect(() => {
        // Always clear any pending timers when isOpen changes.
        if (openDelayTimeoutRef.current !== null) {
            clearTimeout(openDelayTimeoutRef.current);
            openDelayTimeoutRef.current = null;
        }
        if (closeTimeoutRef.current !== null) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
        if (isOpen) {
            setMounted(true);
            // Wait two frames after mount so the off-screen state paints first,
            // then flip to open — CSS transitions kick in for the slide-up.
            openDelayTimeoutRef.current = setTimeout(() => {
                openDelayTimeoutRef.current = null;
                setOpen(true);
            }, 16);
        }
        else {
            setOpen(false);
            // After the close transition completes, unmount.
            closeTimeoutRef.current = setTimeout(() => {
                closeTimeoutRef.current = null;
                translateYRef.current = 0;
                setMounted(false);
            }, 320);
        }
    }, [isOpen]);
    // After mount, install the transition on the sheet/backdrop so subsequent
    // open/close transform changes animate. We do this imperatively (not via
    // React's style prop) so drag handlers can temporarily override transition
    // to "none" without React fighting them on the next render.
    useLayoutEffect(() => {
        if (!mounted)
            return;
        const sheet = sheetRef.current;
        const backdrop = backdropRef.current;
        if (sheet) {
            sheet.style.transition = OPEN_TRANSITION;
            sheet.style.willChange = "transform";
        }
        if (backdrop) {
            backdrop.style.transition = FADE_TRANSITION;
        }
    }, [mounted]);
    // Body scroll lock + pull-to-refresh prevention while the drawer is mounted.
    // overscroll-behavior on html stops iOS Safari's pull-to-refresh; overflow
    // hidden on both html and body prevents background scrolling. Saved values
    // are restored on cleanup.
    useEffect(() => {
        if (!mounted)
            return;
        const html = document.documentElement;
        const body = document.body;
        const prev = {
            htmlOverflow: html.style.overflow,
            bodyOverflow: body.style.overflow,
            htmlOverscroll: html.style.overscrollBehavior,
            bodyOverscroll: body.style.overscrollBehavior,
        };
        html.style.overflow = "hidden";
        body.style.overflow = "hidden";
        html.style.overscrollBehavior = "none";
        body.style.overscrollBehavior = "none";
        return () => {
            html.style.overflow = prev.htmlOverflow;
            body.style.overflow = prev.bodyOverflow;
            html.style.overscrollBehavior = prev.htmlOverscroll;
            body.style.overscrollBehavior = prev.bodyOverscroll;
        };
    }, [mounted]);
    // Native non-passive touchmove on the sheet so we can preventDefault during
    // drag — without this, iOS Safari interprets a downward swipe as
    // pull-to-refresh and reloads the page. React's onTouchMove is passive by
    // default and can't stop the default action.
    useEffect(() => {
        if (!mounted)
            return;
        const sheet = sheetRef.current;
        if (!sheet)
            return;
        const onNativeTouchMove = (e) => {
            if (isDraggingRef.current)
                e.preventDefault();
        };
        sheet.addEventListener("touchmove", onNativeTouchMove, { passive: false });
        return () => {
            sheet.removeEventListener("touchmove", onNativeTouchMove);
        };
    }, [mounted]);
    const setSheetTransform = (yPx) => {
        if (sheetRef.current) {
            sheetRef.current.style.transform = `translate3d(0, ${yPx}px, 0)`;
        }
    };
    const handleTouchStart = (e) => {
        if (!open)
            return; // ignore touches during open/close animation
        if (!checkIsAtTop())
            return;
        if (sheetRef.current) {
            // Disable transition for 1:1 finger tracking.
            sheetRef.current.style.transition = "none";
        }
        touchStartYRef.current = e.touches[0].clientY;
        touchStartTimeRef.current = Date.now();
        translateYRef.current = 0;
        isDraggingRef.current = true;
    };
    const handleTouchMove = (e) => {
        if (!isDraggingRef.current || !sheetRef.current)
            return;
        if (!checkIsAtTop()) {
            isDraggingRef.current = false;
            translateYRef.current = 0;
            setSheetTransform(0);
            return;
        }
        const deltaY = e.touches[0].clientY - touchStartYRef.current;
        if (deltaY <= 0)
            return;
        translateYRef.current = deltaY;
        if (rafIdRef.current === null) {
            rafIdRef.current = requestAnimationFrame(() => {
                rafIdRef.current = null;
                setSheetTransform(translateYRef.current);
            });
        }
    };
    const handleTouchEnd = () => {
        if (!isDraggingRef.current)
            return;
        if (rafIdRef.current !== null) {
            cancelAnimationFrame(rafIdRef.current);
            rafIdRef.current = null;
        }
        const swipeDistance = translateYRef.current;
        const swipeDuration = Date.now() - touchStartTimeRef.current;
        const swipeVelocity = swipeDistance / swipeDuration;
        const shouldClose = swipeDistance > 150 || (swipeVelocity > 0.5 && swipeDistance > 80);
        isDraggingRef.current = false;
        if (shouldClose && checkIsAtTop()) {
            // Continue motion off-screen at velocity, fade backdrop, then onClose.
            const sheet = sheetRef.current;
            const backdrop = backdropRef.current;
            const viewportH = typeof window !== "undefined" ? window.innerHeight : 1000;
            const remaining = Math.max(viewportH - swipeDistance, 0);
            const speed = Math.max(swipeVelocity, 1.8);
            const duration = Math.min(280, Math.max(160, remaining / speed));
            if (sheet) {
                sheet.style.transition = `transform ${duration}ms cubic-bezier(0.32, 0.72, 0, 1)`;
                sheet.style.transform = "translate3d(0, 100%, 0)";
            }
            if (backdrop) {
                backdrop.style.transition = `opacity ${duration}ms ease-out`;
                backdrop.style.opacity = "0";
            }
            if (closeTimeoutRef.current !== null) {
                clearTimeout(closeTimeoutRef.current);
            }
            closeTimeoutRef.current = setTimeout(() => {
                closeTimeoutRef.current = null;
                translateYRef.current = 0;
                setOpen(false);
                setMounted(false);
                onClose();
            }, duration);
            return;
        }
        // Snap back: animate from finger's last position back to 0.
        translateYRef.current = 0;
        if (sheetRef.current) {
            sheetRef.current.style.transition = SNAP_BACK_TRANSITION;
            sheetRef.current.style.transform = "translate3d(0, 0, 0)";
        }
    };
    // Cleanup all timers / rAF on unmount.
    useEffect(() => {
        return () => {
            if (rafIdRef.current !== null)
                cancelAnimationFrame(rafIdRef.current);
            if (closeTimeoutRef.current !== null)
                clearTimeout(closeTimeoutRef.current);
            if (openDelayTimeoutRef.current !== null)
                clearTimeout(openDelayTimeoutRef.current);
        };
    }, []);
    if (!mounted || typeof document === "undefined")
        return null;
    const maxHeightVh = isPaxMode ? "98vh" : "90vh";
    // Transform & opacity are driven by React state. CSS transition (installed
    // imperatively in useLayoutEffect) animates the change. Drag handlers
    // override transform/transition directly on the DOM ref; React doesn't
    // fight them because the prop value here doesn't change during drag.
    const sheetTransform = open
        ? "translate3d(0, 0, 0)"
        : "translate3d(0, 100%, 0)";
    const backdropOpacity = open ? 1 : 0;
    return createPortal(_jsxs(_Fragment, { children: [_jsx(Box, { ref: backdropRef, position: "fixed", top: "0", left: "0", right: "0", bottom: "0", bg: "rgba(0, 0, 0, 0.5)", zIndex: 99998, style: { opacity: backdropOpacity, touchAction: "none" }, onClick: onClose }), _jsxs(Box, { ref: sheetRef, position: "fixed", bottom: 0, left: 0, right: 0, bg: bg, color: color, borderTopRadius: 24, pt: 4, maxH: maxHeightVh, h: defaultHeight !== null && defaultHeight !== void 0 ? defaultHeight : "auto", zIndex: 99999, display: "flex", flexDirection: "column", overflow: "hidden", style: { transform: sheetTransform, touchAction: "none" }, onTouchStart: handleTouchStart, onTouchMove: handleTouchMove, onTouchEnd: handleTouchEnd, onTouchCancel: handleTouchEnd, children: [_jsx(Flex, { w: "100%", alignItems: "center", justifyContent: "center", position: "absolute", top: 4, zIndex: 99999, pointerEvents: "none", children: _jsx(Box, { w: "50px", h: "3px", bg: "#7e7e7e", borderRadius: 10 }) }), showCloseButton && (_jsx(CloseButton, { position: "absolute", top: 2, right: 2, zIndex: 99999, color: color, onClick: onClose })), header && (_jsx(Box, { px: 6, py: 4, fontSize: "lg", fontWeight: "semibold", textAlign: "center", flexShrink: 0, children: header })), _jsxs(Box, { ref: bodyRef, flex: "1 1 auto", minH: 0, overflow: "auto", p: 0, position: "relative", style: { touchAction: "pan-y", overscrollBehavior: "contain" }, children: [(topText === null || topText === void 0 ? void 0 : topText.trim()) && (_jsx(Flex, { position: "absolute", top: "-35px", right: "16px", align: "center", px: "12px", py: "8px", bg: "rgba(90,60,180,0.9)", backdropFilter: "blur(8px)", borderRadius: "full", cursor: "pointer", zIndex: 999999, boxShadow: "0 0 12px rgba(168,85,247,0.6)", _hover: {
                                    boxShadow: "0 0 18px rgba(168,85,247,0.85)",
                                }, transition: "all 0.18s ease", onClick: onClose, children: _jsx(Box, { fontSize: "10px", fontWeight: "500", letterSpacing: "0.6px", textTransform: "uppercase", color: "white", userSelect: "none", lineHeight: "1", children: topText }) })), content] }), footer && (_jsx(Box, { px: 6, py: 4, display: "flex", alignItems: "center", justifyContent: "flex-end", flexShrink: 0, children: footer }))] })] }), document.body);
};
export default SwipeDownDrawer;
